"use client";

/**
 * DialIQ — Unreachable Badge
 *
 * Rendered next to a lead's name when they've been flagged
 * `unreachable_needs_review` by DialIQ's 3-cycle terminal state.
 * Clicking it opens a popover with:
 *   - What happened (3 cycles × 3 windows, ~45 attempts)
 *   - Action: Remove tag (re-enter redial queue)
 *   - Action: Move to DNC
 */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertOctagon } from "lucide-react";
import api from "@/app/_module/services/api";
import { successToast, errorToast } from "@/app/_module/services/helper";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/app/_module/components/ui/dialog";
import { Button } from "@/app/_module/components/ui/button";
import { cn } from "@/app/_module/lib/utils";
import { focusRing } from "@/app/(authenticated)/settings/a2p-verification/_components/overview/primitives";

export const UNREACHABLE_TAG = "unreachable_needs_review";

interface UnreachableBadgeProps {
  leadId: string;
  tags?: string[] | null;
  leadName?: string | null;
  compact?: boolean;
  onUpdated?: () => void;
}

export function UnreachableBadge({
  leadId,
  tags,
  leadName,
  compact = false,
  onUpdated,
}: UnreachableBadgeProps) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  // CRITICAL: all hooks must be declared BEFORE any early return. The earlier
  // version placed these useMutation calls below `if (!isUnreachable) return null`
  // — when the tag was cleared via the mutation, the next render took the
  // early-return path and React saw fewer hooks than the prior render
  // → "Rendered fewer hooks than during the previous render" crash.
  const removeMutation = useMutation({
    mutationFn: async () => {
      const nextTags = (tags || []).filter((t) => t !== UNREACHABLE_TAG);
      await api.patch({
        url: `/api/leads/${leadId}`,
        body: { tags: nextTags },
      });
    },
    onSuccess: () => {
      successToast("Back in redial. DialIQ can queue this lead again.");
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["redial"] });
      setOpen(false);
      onUpdated?.();
    },
    onError: () => errorToast("Couldn't update lead"),
  });

  const dncMutation = useMutation({
    mutationFn: async () => {
      await api.patch({
        url: `/api/leads/${leadId}`,
        body: { status: "Do not call", leadStat: "Do not call" },
      });
    },
    onSuccess: () => {
      successToast("Moved to Do Not Call");
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["redial"] });
      setOpen(false);
      onUpdated?.();
    },
    onError: () => errorToast("Couldn't update status"),
  });

  const isUnreachable = !!tags?.includes(UNREACHABLE_TAG);
  if (!isUnreachable) return null;

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        aria-label={`${leadName || "Lead"} is marked unreachable. Review`}
        className={cn(
          "inline-flex items-center gap-1 rounded-md border border-rose-200 bg-rose-50 font-medium text-rose-700 transition-colors hover:border-rose-300 hover:bg-rose-100",
          compact ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-[11px]",
          focusRing,
        )}
      >
        <AlertOctagon className={compact ? "h-2.5 w-2.5" : "h-3 w-3"} />
        Unreachable
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md md:max-w-md lg:max-w-md xl:max-w-md">
          <DialogHeader>
            <DialogTitle className="pr-6 !text-[17px] leading-6">
              {leadName || "This lead"} couldn&apos;t be reached
            </DialogTitle>
            <DialogDescription>
              No answer in the morning, afternoon or evening windows, 3 rounds in a row. DialIQ
              took the lead out of redial.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              onClick={() => dncMutation.mutate()}
              disabled={dncMutation.isPending || removeMutation.isPending}
              className={cn("text-rose-700 hover:bg-rose-50 hover:text-rose-800", focusRing)}
            >
              {dncMutation.isPending ? "Moving…" : "Move to Do Not Call"}
            </Button>
            <Button
              onClick={() => removeMutation.mutate()}
              disabled={removeMutation.isPending || dncMutation.isPending}
              className={focusRing}
            >
              {removeMutation.isPending ? "Putting back…" : "Put back in redial"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default UnreachableBadge;
